import React, { useState } from "react";
import {
  Box,
  Button,
  InputAdornment,
  Link,
  Stack,
  TextField,
  IconButton,
  Alert,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import LockOutlineRoundedIcon from "@mui/icons-material/LockOutlineRounded";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { ROUTES } from "../../../shared/utils";
import { AuthCardIntro } from "./AuthCardHeader";
import { useLogin } from "../mutations";
import { NewPasswordSetup } from "./NewPasswordSetup";
interface LoginFormProps {
  onSuccess: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState("");
  const [showNewPasswordSetup, setShowNewPasswordSetup] = useState(false);
  const [loginSession, setLoginSession] = useState("");

  const loginMutation = useLogin();

  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isPasswordValid = password.length >= 6;
  const showEmailError = touched && !isEmailValid;
  const showPasswordError = touched && !isPasswordValid;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTouched(true);
    setError("");

    if (!isEmailValid || !isPasswordValid) {
      return;
    }

    try {
      const result = await loginMutation.mutateAsync({
        username: email.trim(),
        password: password.trim(),
      });

      if (result.data.challenge_name === "NEW_PASSWORD_REQUIRED") {
        setLoginSession(result.data.session || "");
        setShowNewPasswordSetup(true);
      } else {
        onSuccess();
      }
    } catch (error: unknown) {
      let errorMessage = "Login failed. Please try again.";

      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as {
          response?: { status?: number; data?: { message?: string } };
        };

        if (axiosError.response?.status === 401) {
          errorMessage =
            "Invalid credentials. Please check your email and password.";
        } else if (axiosError.response?.data?.message) {
          errorMessage = axiosError.response.data.message;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      setError(errorMessage);
    }
  };

  const handleNewPasswordSuccess = () => {
    onSuccess();
  };

  const handleNewPasswordError = (error: string) => {
    setError(error);
    setShowNewPasswordSetup(false);
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  if (showNewPasswordSetup) {
    return (
      <NewPasswordSetup
        username={email}
        session={loginSession}
        onSuccess={handleNewPasswordSuccess}
        onError={handleNewPasswordError}
      />
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack spacing={4}>
        <AuthCardIntro
          title="Welcome Back"
          subtitle="Enter your credentials to proceed"
        />
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={2}>
          <TextField
            fullWidth
            label="Email address"
            value={email}
            type="email"
            onChange={(event) => setEmail(event.target.value)}
            onBlur={() => setTouched(true)}
            error={showEmailError}
            placeholder="Enter your email address"
            helperText={
              showEmailError ? "Please enter a valid email address." : ""
            }
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MailOutlineRoundedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label="Password"
            value={password}
            type={showPassword ? "text" : "password"}
            onChange={(event) => setPassword(event.target.value)}
            onBlur={() => setTouched(true)}
            error={showPasswordError}
            placeholder="Enter your password"
            helperText={
              showPasswordError
                ? "Password must be at least 6 characters long."
                : ""
            }
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlineRoundedIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleTogglePasswordVisibility}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Stack>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Link
            component={RouterLink}
            to={ROUTES.RESET_PASSWORD}
            variant="body2"
            underline="hover"
            sx={{ fontWeight: 500 }}
          >
            Forgot password?
          </Link>
        </Box>
        <Button
          type="submit"
          size="large"
          variant="contained"
          fullWidth
          endIcon={<ArrowForwardRoundedIcon />}
          disabled={
            !email.length || !password.length || loginMutation.isPending
          }
          sx={{ mt: 4, mb: 2 }}
        >
          {loginMutation.isPending ? "Signing in..." : "Sign In"}
        </Button>
      </Stack>
    </Box>
  );
};
